import type { IPageLink } from '@/sanity/globals/CTA';
import type { IPageSettings } from '@/sanity/globals/PageGlobals';
import type { IPromoBanner, ITopBanner } from '@/sanity/lib/definitions';
import type { IAffiliateDiscountSettings } from '@/sanity/templates';

export interface IResolvedAffiliateBanners {
  topBanner?: ITopBanner;
  promoBanner?: IPromoBanner;
}

/**
 * Both banners come from the global settings (or the page settings) with their
 * own generic checkout link, so a click from the top banner or the promo popup
 * would drop the partner's coupon / `?aff=` params. Repointing them at the page
 * CTA keeps the promo code — the same reason the summer sale and birthday
 * banners already take the page `ctaUrl`.
 *
 * Only the destination changes: text, labels, image and button styling stay as
 * the editor set them. A CTA the editor left empty stays empty — we never
 * invent one — and popup/anchor CTAs keep working as before, since we rewrite
 * the link and not the action.
 */
export function withAffiliateBannersCta(
  banners: IResolvedAffiliateBanners,
  url: string | undefined,
): IResolvedAffiliateBanners {
  if (!url) return banners;

  // `externalLink` has to flip too: an internal-link CTA would otherwise
  // resolve through the referenced page slug and ignore our url.
  const withUrl = (link?: IPageLink): IPageLink | undefined =>
    link ? { ...link, externalLink: true, url } : link;

  const { topBanner, promoBanner } = banners;
  const promoCta = promoBanner?.button?.cta;

  return {
    topBanner: topBanner
      ? { ...topBanner, ctaLink: withUrl(topBanner.ctaLink) }
      : topBanner,
    promoBanner:
      promoBanner && promoCta
        ? {
            ...promoBanner,
            button: {
              ...promoBanner.button,
              cta: { ...promoCta, link: withUrl(promoCta.link) },
            },
          }
        : promoBanner,
  };
}
